import { __rest } from "tslib";
import React from 'react';
import classnames from 'classnames';
import { parseUrl, urlToString } from 'Shared/url';
import { getSizes, getSizesEx } from 'Shared/ImageSizes';
import preload, { hasOtherSizeLoaded, getBestCachedImageUrl, registerLoaded } from 'Shared/ImageFallback/preload';
import * as styles from './base.scss';
const noImage = '/styles/images/missingImage.svg';
const sizes = getSizes();
const sizesEx = getSizesEx();
export function srcset(urlString, requestedSize = null, maxSize = null) {
    const srcset = [];
    Object.keys(sizes).forEach(size => {
        if ((!requestedSize || sizes[size] >= sizes[requestedSize]) && (!maxSize || sizes[size] <= sizes[maxSize])) {
            srcset.push(urlWithSize(urlString, size) + ' ' + sizes[size] + 'w');
        }
    });
    return srcset.join(', ');
}
export function urlWithSize(urlString, size) {
    const url = parseUrl(urlString);
    url.query['w'] = sizes[size];
    url.query['h'] = sizes[size];
    url.query['mode'] = 'max';
    url.query['quality'] = '75';
    return urlToString(url);
}
export function urlWithSizeEx(urlString, size, mode) {
    const url = parseUrl(urlString);
    url.query['w'] = sizesEx[size].w;
    url.query['h'] = sizesEx[size].h;
    url.query['mode'] = !!mode ? mode : 'max';
    url.query['quality'] = '70';
    return urlToString(url);
}
export default class ImageFallback extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            useCachedImagePlaceHolder: props.preferSmallerIfCached && hasOtherSizeLoaded(props.src, props.size, props.minSizeIfCached),
            stretchPlaceHolderOnWidth: false,
        };
    }
    onLoad(e) {
        const image = e.target;
        setCachedIfLoaded(image, this.props.src, this.props.size);
        if (this.props.fadeIn && !this.state.useCachedImagePlaceHolder) {
            image.classList.remove(styles.fadedOut);
        }
    }
    sizes() {
        if (this.props.size !== 'original') {
            return sizes[this.props.size] + 'px';
        }
        else {
            return null;
        }
    }
    srcSet() {
        if (this.props.size !== 'original') {
            return srcset(this.props.src, this.props.size, this.props.maxSize);
        }
        else {
            return srcset(this.props.src, 'tiny');
        }
    }
    setPlaceholderStretchAxisFromDimensions(width, height) {
        this.setState({ stretchPlaceHolderOnWidth: width > height });
    }
    componentDidMount() {
        if (this.state.useCachedImagePlaceHolder) {
            this.setPlaceholderStretchAxisFromDimensions(this.placeHolderImage.naturalWidth, this.placeHolderImage.naturalHeight);
            preload(this.props.src, this.props.size, this.srcSet()).then(() => {
                if (this.state.useCachedImagePlaceHolder) {
                    this.setState({ useCachedImagePlaceHolder: false });
                }
            });
        }
    }
    render() {
        const _a = this.props, { className, size, maxSize, skipSrcSet, preferSmallerIfCached, fadeIn, minSizeIfCached } = _a, restProps = __rest(_a, ["className", "size", "maxSize", "skipSrcSet", "preferSmallerIfCached", "fadeIn", "minSizeIfCached"]);
        const newProps = Object.assign({}, restProps);
        const noImageSrc = this.props.noImageSrc ? this.props.noImageSrc : noImage;
        if (!this.props.src) {
            return (React.createElement("div", { className: styles.noImageHolder },
                React.createElement("img", Object.assign({}, newProps, { className: classnames(className, styles.missingImage, styles.disableClicks), src: noImageSrc }))));
        }
        if (this.props.size !== 'original') {
            newProps.src = urlWithSize(this.props.src, this.props.size);
        }
        if (this.state.useCachedImagePlaceHolder) {
            return (React.createElement("img", Object.assign({}, newProps, { ref: el => (this.placeHolderImage = el), src: getBestCachedImageUrl(this.props.src), className: classnames(className, this.state.stretchPlaceHolderOnWidth ? styles.stretchOnWidth : styles.stretchOnHeigt, styles.disableClicks) })));
        }
        else {
            return (React.createElement("img", Object.assign({}, newProps, { className: classnames(className, styles.disableClicks), srcSet: this.props.skipSrcSet ? null : this.srcSet(), sizes: this.props.skipSrcSet ? null : this.sizes(), onLoad: e => this.onLoad(e), ref: img => {
                    if (img) {
                        setCachedIfLoaded(img, this.props.src, this.props.size);
                        if (imageHasFailedToLoad(img)) {
                            setTimeout(() => {
                                // Firefox has an ugly tendency to report naturalWidth as 0 when a SW is active.
                                // But if we wait just a little bit, the sizes have been calculated. Broken
                                // images should be uncommon, so we can afford the setTimeout here
                                if (imageHasFailedToLoad(img)) {
                                    setErrorImage(img);
                                }
                            });
                        }
                        if (this.props.fadeIn && !this.props.preferSmallerIfCached && !imageHasLoaded(img)) {
                            img.classList.add(styles.fadedOut);
                            img.classList.add(styles.base);
                        }
                    }
                }, onError: e => onError(e) })));
        }
    }
}
ImageFallback.defaultProps = {
    size: 'original',
};
function setCachedIfLoaded(img, originalSrc, size) {
    // If it's `noImage`
    if (!originalSrc) {
        return;
    }
    if (imageHasLoaded(img)) {
        registerLoaded(originalSrc, img.currentSrc, size);
    }
}
function onError(e) {
    const img = e.target;
    e.currentTarget.classList.add(styles.missingImage);
    setErrorImage(img);
}
function setErrorImage(img) {
    img.src = (window.document.location.hostname == "localhost") && img.src.includes(window.location.host) ? img.src.replace(window.location.host, 'int.menigo.se') : noImage;
    if ((window.document.location.hostname == "localhost") && img.src != noImage)
        img.classList.remove(styles.missingImage);
    img.classList.add(styles.base);
    img.srcset = '';
    img.sizes = '';
}
function imageHasLoaded(img) {
    if (!img.complete) {
        return false;
    }
    if (typeof img.naturalWidth !== 'undefined' && img.naturalWidth === 0) {
        return false;
    }
    // No other way of checking: assume it’s ok.
    return true;
}
function imageHasFailedToLoad(img) {
    // Firefox and IE has a bug where svg reports 0 for natural size in some cases
    if (img.src && parseUrl(img.src).path.endsWith('.svg')) {
        return false;
    }
    return img.complete && typeof img.naturalWidth !== 'undefined' && img.naturalWidth === 0;
}
